<template>
  <div class="animated-characters">
    <div ref="purple" class="character purple" :style="purpleStyle">
      <div class="eye-row" :style="purpleEyeRowStyle">
        <div class="eye-ball" :style="eyeBallStyle(18, isPurpleBlinking)">
          <div v-if="!isPurpleBlinking" class="pupil" :style="purplePupilStyle"></div>
        </div>
        <div class="eye-ball" :style="eyeBallStyle(18, isPurpleBlinking)">
          <div v-if="!isPurpleBlinking" class="pupil" :style="purplePupilStyle"></div>
        </div>
      </div>
    </div>

    <div ref="black" class="character black" :style="blackStyle">
      <div class="eye-row" :style="blackEyeRowStyle">
        <div class="eye-ball" :style="eyeBallStyle(16, isBlackBlinking)">
          <div v-if="!isBlackBlinking" class="pupil" :style="blackPupilStyle"></div>
        </div>
        <div class="eye-ball" :style="eyeBallStyle(16, isBlackBlinking)">
          <div v-if="!isBlackBlinking" class="pupil" :style="blackPupilStyle"></div>
        </div>
      </div>
    </div>

    <div ref="orange" class="character orange" :style="orangeStyle">
      <div class="eye-row orange-eyes" :style="orangeEyeRowStyle">
        <div class="bare-pupil" :style="orangePupilStyle"></div>
        <div class="bare-pupil" :style="orangePupilStyle"></div>
      </div>
    </div>

    <div ref="yellow" class="character yellow" :style="yellowStyle">
      <div class="eye-row yellow-eyes" :style="yellowEyeRowStyle">
        <div class="bare-pupil" :style="yellowPupilStyle"></div>
        <div class="bare-pupil" :style="yellowPupilStyle"></div>
      </div>
      <div class="mouth" :style="yellowMouthStyle"></div>
    </div>
  </div>
</template>

<script>
const DEFAULT_POS = { faceX: 0, faceY: 0, bodySkew: 0 };

export default {
  name: "AnimatedCharacters",
  props: {
    isTyping: {
      type: Boolean,
      default: false,
    },
    showPassword: {
      type: Boolean,
      default: false,
    },
    passwordLength: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      mouseX: 0,
      mouseY: 0,
      purplePos: { ...DEFAULT_POS },
      blackPos: { ...DEFAULT_POS },
      orangePos: { ...DEFAULT_POS },
      yellowPos: { ...DEFAULT_POS },
      isPurpleBlinking: false,
      isBlackBlinking: false,
      isLookingAtEachOther: false,
      isPurplePeeking: false,
      purpleBlinkTimer: null,
      blackBlinkTimer: null,
      lookTimer: null,
      peekTimer: null,
      peekResetTimer: null,
    };
  },
  computed: {
    isHidingPassword() {
      return this.passwordLength > 0 && !this.showPassword;
    },
    purpleStyle() {
      let transform = `skewX(${this.purplePos.bodySkew}deg)`;
      if (this.passwordLength > 0 && this.showPassword) {
        transform = "skewX(0deg)";
      } else if (this.isTyping || this.isHidingPassword) {
        transform = `skewX(${this.purplePos.bodySkew - 12}deg) translateX(40px)`;
      }
      return {
        left: "70px",
        width: "180px",
        height: this.isTyping || this.isHidingPassword ? "440px" : "400px",
        backgroundColor: "#6C3FF5",
        borderRadius: "10px 10px 0 0",
        zIndex: 1,
        transform,
      };
    },
    purpleEyeRowStyle() {
      const left = this.passwordLength > 0 && this.showPassword
        ? 20
        : this.isLookingAtEachOther
          ? 55
          : 45 + this.purplePos.faceX;
      const top = this.passwordLength > 0 && this.showPassword
        ? 35
        : this.isLookingAtEachOther
          ? 65
          : 40 + this.purplePos.faceY;
      return { left: `${left}px`, top: `${top}px`, gap: "32px" };
    },
    purplePupilStyle() {
      const forced = this.passwordLength > 0 && this.showPassword
        ? { x: this.isPurplePeeking ? 4 : -4, y: this.isPurplePeeking ? 5 : -4 }
        : this.isLookingAtEachOther
          ? { x: 3, y: 4 }
          : { x: this.clamp(this.purplePos.faceX / 2, -5, 5), y: this.clamp(this.purplePos.faceY / 2, -5, 5) };
      return this.pupilStyle(7, forced.x, forced.y);
    },
    blackStyle() {
      let transform = `skewX(${this.blackPos.bodySkew}deg)`;
      if (this.passwordLength > 0 && this.showPassword) {
        transform = "skewX(0deg)";
      } else if (this.isLookingAtEachOther) {
        transform = `skewX(${this.blackPos.bodySkew * 1.5 + 10}deg) translateX(20px)`;
      } else if (this.isTyping || this.isHidingPassword) {
        transform = `skewX(${this.blackPos.bodySkew * 1.5}deg)`;
      }
      return {
        left: "240px",
        width: "120px",
        height: "310px",
        backgroundColor: "#2D2D2D",
        borderRadius: "8px 8px 0 0",
        zIndex: 2,
        transform,
      };
    },
    blackEyeRowStyle() {
      const left = this.passwordLength > 0 && this.showPassword
        ? 10
        : this.isLookingAtEachOther
          ? 32
          : 26 + this.blackPos.faceX;
      const top = this.passwordLength > 0 && this.showPassword
        ? 28
        : this.isLookingAtEachOther
          ? 12
          : 32 + this.blackPos.faceY;
      return { left: `${left}px`, top: `${top}px`, gap: "24px" };
    },
    blackPupilStyle() {
      const forced = this.passwordLength > 0 && this.showPassword
        ? { x: -4, y: -4 }
        : this.isLookingAtEachOther
          ? { x: 0, y: -4 }
          : { x: this.clamp(this.blackPos.faceX / 2, -4, 4), y: this.clamp(this.blackPos.faceY / 2, -4, 4) };
      return this.pupilStyle(6, forced.x, forced.y);
    },
    orangeStyle() {
      return {
        left: "0px",
        width: "240px",
        height: "200px",
        zIndex: 3,
        backgroundColor: "#FF9B6B",
        borderRadius: "120px 120px 0 0",
        transform: this.passwordLength > 0 && this.showPassword
          ? "skewX(0deg)"
          : `skewX(${this.orangePos.bodySkew}deg)`,
      };
    },
    orangeEyeRowStyle() {
      return {
        left: `${this.passwordLength > 0 && this.showPassword ? 50 : 82 + this.orangePos.faceX}px`,
        top: `${this.passwordLength > 0 && this.showPassword ? 85 : 90 + this.orangePos.faceY}px`,
        gap: "32px",
      };
    },
    orangePupilStyle() {
      const x = this.passwordLength > 0 && this.showPassword
        ? -5
        : this.clamp(this.orangePos.faceX / 2, -5, 5);
      const y = this.passwordLength > 0 && this.showPassword
        ? -4
        : this.clamp(this.orangePos.faceY / 2, -5, 5);
      return this.pupilStyle(12, x, y);
    },
    yellowStyle() {
      return {
        left: "310px",
        width: "140px",
        height: "230px",
        backgroundColor: "#E8D754",
        borderRadius: "70px 70px 0 0",
        zIndex: 4,
        transform: this.passwordLength > 0 && this.showPassword
          ? "skewX(0deg)"
          : `skewX(${this.yellowPos.bodySkew}deg)`,
      };
    },
    yellowEyeRowStyle() {
      return {
        left: `${this.passwordLength > 0 && this.showPassword ? 20 : 52 + this.yellowPos.faceX}px`,
        top: `${this.passwordLength > 0 && this.showPassword ? 35 : 40 + this.yellowPos.faceY}px`,
        gap: "24px",
      };
    },
    yellowPupilStyle() {
      const x = this.passwordLength > 0 && this.showPassword
        ? -5
        : this.clamp(this.yellowPos.faceX / 2, -5, 5);
      const y = this.passwordLength > 0 && this.showPassword
        ? -4
        : this.clamp(this.yellowPos.faceY / 2, -5, 5);
      return this.pupilStyle(12, x, y);
    },
    yellowMouthStyle() {
      return {
        left: `${this.passwordLength > 0 && this.showPassword ? 10 : 40 + this.yellowPos.faceX}px`,
        top: `${this.passwordLength > 0 && this.showPassword ? 88 : 88 + this.yellowPos.faceY}px`,
      };
    },
  },
  watch: {
    isTyping(val) {
      clearTimeout(this.lookTimer);
      if (val) {
        this.isLookingAtEachOther = true;
        this.lookTimer = setTimeout(() => {
          this.isLookingAtEachOther = false;
        }, 800);
      } else {
        this.isLookingAtEachOther = false;
      }
    },
    showPassword() {
      this.schedulePeek();
    },
    passwordLength() {
      this.schedulePeek();
    },
  },
  mounted() {
    window.addEventListener("mousemove", this.handleMouseMove);
    this.startBlink("purple");
    this.startBlink("black");
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.handleMouseMove);
    clearTimeout(this.purpleBlinkTimer);
    clearTimeout(this.blackBlinkTimer);
    clearTimeout(this.lookTimer);
    clearTimeout(this.peekTimer);
    clearTimeout(this.peekResetTimer);
  },
  methods: {
    clamp(v, min, max) {
      return Math.max(min, Math.min(max, v));
    },
    pupilStyle(size, x, y) {
      return {
        width: `${size}px`,
        height: `${size}px`,
        transform: `translate(${x}px, ${y}px)`,
      };
    },
    eyeBallStyle(size, isBlinking) {
      return {
        width: `${size}px`,
        height: isBlinking ? "2px" : `${size}px`,
      };
    },
    handleMouseMove(e) {
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;
      this.updateCharacterPos();
    },
    updateCharacterPos() {
      this.purplePos = this.calculatePos(this.$refs.purple);
      this.blackPos = this.calculatePos(this.$refs.black);
      this.orangePos = this.calculatePos(this.$refs.orange);
      this.yellowPos = this.calculatePos(this.$refs.yellow);
    },
    calculatePos(el) {
      if (!el || !el.getBoundingClientRect) {
        return { ...DEFAULT_POS };
      }
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 3;
      const deltaX = this.mouseX - centerX;
      const deltaY = this.mouseY - centerY;
      return {
        faceX: this.clamp(deltaX / 20, -15, 15),
        faceY: this.clamp(deltaY / 30, -10, 10),
        bodySkew: this.clamp(-deltaX / 120, -6, 6),
      };
    },
    randomBlinkDelay() {
      return Math.random() * 4000 + 3000;
    },
    startBlink(type) {
      const delay = this.randomBlinkDelay();
      const timerKey = type === "purple" ? "purpleBlinkTimer" : "blackBlinkTimer";
      this[timerKey] = setTimeout(() => {
        if (type === "purple") {
          this.isPurpleBlinking = true;
        } else {
          this.isBlackBlinking = true;
        }
        setTimeout(() => {
          if (type === "purple") {
            this.isPurpleBlinking = false;
          } else {
            this.isBlackBlinking = false;
          }
          this.startBlink(type);
        }, 150);
      }, delay);
    },
    schedulePeek() {
      clearTimeout(this.peekTimer);
      clearTimeout(this.peekResetTimer);
      if (!(this.passwordLength > 0 && this.showPassword)) {
        this.isPurplePeeking = false;
        return;
      }
      const delay = Math.random() * 3000 + 2000;
      this.peekTimer = setTimeout(() => {
        this.isPurplePeeking = true;
        this.peekResetTimer = setTimeout(() => {
          this.isPurplePeeking = false;
          this.schedulePeek();
        }, 800);
      }, delay);
    },
  },
};
</script>

<style scoped>
.animated-characters {
  position: relative;
  width: 550px;
  height: 400px;
}

.character {
  position: absolute;
  bottom: 0;
  transform-origin: bottom center;
  transition: all 0.7s ease-in-out;
}

.eye-row {
  position: absolute;
  display: flex;
  transition: all 0.7s ease-in-out;
}

.eye-ball {
  border-radius: 50%;
  background: #fff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.pupil {
  border-radius: 50%;
  background: #2d2d2d;
  transition: transform 0.1s ease-out;
}

.bare-pupil {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #2d2d2d;
  transition: transform 0.1s ease-out;
}

.mouth {
  position: absolute;
  width: 80px;
  height: 4px;
  border-radius: 999px;
  background: #2d2d2d;
  transition: all 0.2s ease-out;
}
</style>
